import { Extra } from '../models/types';

export function sortExtrasByCatalogOrder(
    selectedExtras: Extra[],
    catalogExtras: Extra[]
): Extra[] {
    if (catalogExtras.length === 0 || selectedExtras.length <= 1) {
        return selectedExtras;
    }

    const orderById = new Map(
        catalogExtras.map((extra, index) => [extra.extra_id, index])
    );

    return [...selectedExtras].sort((a, b) => {
        const orderA = orderById.get(a.extra_id) ?? Number.MAX_SAFE_INTEGER;
        const orderB = orderById.get(b.extra_id) ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
    });
}

export function parsePrice(value: number | string | null | undefined): number {
    if (value === null || value === undefined || value === '') return 0;
    const parsed = typeof value === 'number' ? value : parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
}

function parseAmount(value: number | string | null | undefined): number {
    if (value === null || value === undefined || value === '') return 0;
    const parsed = typeof value === 'number' ? value : parseInt(String(value), 10);
    return Number.isNaN(parsed) ? 0 : parsed;
}

export function getDefaultExtraAmount(extra: Extra): number {
    const minUnits = parseAmount(extra.min_units);
    if (minUnits > 0) {
        return minUnits;
    }
    return 1;
}

export function getSelectedExtraAmount(extra: Extra): number {
    const amount = parseAmount(extra.extra_amount);
    if (amount > 0) {
        return amount;
    }
    return getDefaultExtraAmount(extra);
}

function getExtraAmount(extra: Extra): number {
    return getSelectedExtraAmount(extra);
}

function hasTieredPricing(extra: Extra): boolean {
    return extra.price_unit != null && extra.min_units != null && extra.min_units > 0;
}

export function calculateExtraTotal(extra: Extra): number {
    if (extra.info_type === 'ask_shop') {
        return 0;
    }

    const amount = getExtraAmount(extra);
    const unitPrice = parsePrice(extra.price);

    // Mirrors backend step3_calculateExtraTotalPrice tiered pricing
    if (hasTieredPricing(extra)) {
        const minUnits = parseAmount(extra.min_units);
        if (amount <= minUnits) {
            return unitPrice;
        }
        return unitPrice + (amount - minUnits) * parsePrice(extra.price_unit);
    }

    // Simple per-unit pricing (e.g. Niño Extra: 12 € × 3 = 36 €)
    return amount * unitPrice;
}

export function formatExtraPriceDisplay(
    extra: Extra,
    currencyFormatter: Intl.NumberFormat
): string {
    if (extra.info_type === 'ask_shop') {
        return 'Consultar con centro';
    }

    if (extra.extra_cost_str?.startsWith('Desde') && getExtraAmount(extra) <= 1) {
        return extra.extra_cost_str;
    }

    return currencyFormatter.format(calculateExtraTotal(extra));
}

export function formatExtraUnitsLabel(extra: Extra): string {
    if (extra.info_type !== 'dialog_int_amount') {
        return '';
    }

    const amount = getSelectedExtraAmount(extra);
    if (amount <= 1) {
        return amount === 1 ? '1 unidad' : '';
    }

    return `${amount} unidades`;
}

export function formatExtraUnitsSuffix(extra: Extra): string {
    const amount = getExtraAmount(extra);
    return amount > 1 ? ` (${amount} unidades)` : '';
}
